import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useId, useRef, useState } from 'react'

import { ApiError } from '../../../services/api/client'
import { Button } from '../../../ui/Button/Button'
import { Switch } from '../../../ui/Switch/Switch'
import {
  PATIENT_REMINDER_PREFERENCE_QUERY_ROOT,
  type PatientReminderPreference as Preference,
  type PatientReminderPreferenceChange,
  type PatientReminderPreferenceRepository,
} from '../patient-reminder-preference-repository'
import styles from '../PatientsPage.module.css'

export const REMINDER_PREFERENCE_TITLE = 'Appointment reminders'
export const REMINDER_PREFERENCE_HINT =
  'Texts the evening before confirmed appointments at this clinic'

// One record can sit under two Dashboard patient ids (a re-bound clinic), so a
// change invalidates the whole tenant prefix, never just this patient's key.
const QUERY_ROOT = PATIENT_REMINDER_PREFERENCE_QUERY_ROOT
// The endpoint counts other patients on the number up to this many
// (user-management MAX_PHONE_GROUP); at the cap it is a lower bound.
const OTHER_PATIENTS_CAP = 50

const STATE_LABEL: Record<Preference['preferenceState'], string> = {
  DEFAULT_ON: 'On (default)',
  ON: 'On',
  OFF: 'Off',
  UNREADABLE: 'Unavailable',
}

function statusLine(preference: Preference) {
  switch (preference.preferenceState) {
    case 'OFF':
      return "Off for this patient. Flio won't send them appointment reminders."
    case 'UNREADABLE':
      return "This setting can't be read right now, so no reminders are sent. Contact Flio support."
    default:
      return "On for this patient. A reminder can be sent when the clinic's reminders are on and their number hasn't opted out."
  }
}

function contextLines(preference: Preference) {
  const lines: string[] = []
  if (!preference.clinicRemindersOn)
    lines.push(
      'Appointment reminders are off for this clinic, so none are sent right now.'
    )
  if (preference.textingState === 'OPTED_OUT')
    lines.push(
      'This number is opted out of texts from this clinic, so no reminders go to it. That applies to everyone who uses this number.'
    )
  if (preference.textingState === 'NO_PHONE')
    lines.push("No mobile number on file, so reminders can't be sent.")
  if (preference.textingState === 'UNREADABLE')
    lines.push(
      "The texting status for this number can't be read, so reminders can't be sent."
    )
  const others = preference.otherPatientsOnPhone
  if (others !== null && others > 0)
    lines.push(
      `${others >= OTHER_PATIENTS_CAP ? `${OTHER_PATIENTS_CAP} or more other patients use` : `${others} other ${others === 1 ? 'patient uses' : 'patients use'}`} this number. Their reminders aren't affected; change each patient separately.`
    )
  return lines
}

const FOOTNOTE =
  "This only changes Flio's appointment reminders. It isn't an opt-out: other texts from the clinic still go, and reminders sent by other systems aren't affected. A patient who wants no texts at all should reply STOP."

function saveErrorMessage(error: unknown) {
  if (error instanceof ApiError && error.status === 409) {
    if (error.reason === 'REMINDER_PREFERENCE_CONFLICT')
      return 'This setting changed. Showing the current value.'
    if (error.reason === 'REMINDER_PREFERENCE_UNREADABLE')
      return "This setting can't be changed right now. Contact Flio support."
  }
  if (error instanceof ApiError && error.status === 403)
    return "You don't have permission to change this setting."
  return "The setting couldn't be saved. Try again."
}

function focusSwitch(target: HTMLElement | null) {
  target?.querySelector('input')?.focus()
}

function samePatient(
  preference: Preference,
  tenantId: string,
  patientAuthorityId: string
) {
  return (
    preference.tenantUid === tenantId &&
    preference.patientAuthorityId === patientAuthorityId
  )
}

// Staff's per-patient Appointment Reminder preference. It only stops Flio's
// reminders and is never consent: the clinic's setting and the number's STOP
// state are explained beside it but never block recording it. Every change is
// confirmed first and sent with the revision staff saw, so a concurrent change
// is a conflict rather than an overwrite.
export function PatientReminderPreference({
  tenantId,
  patientAuthorityId,
  displayName,
  repository,
}: {
  tenantId: string
  patientAuthorityId: string
  displayName: string
  repository: PatientReminderPreferenceRepository
}) {
  const client = useQueryClient()
  const statusId = useId()
  const confirmationTitleId = useId()
  const confirmationBodyId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const switchTargetRef = useRef<HTMLLabelElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const restoreFocus = useRef(false)
  const [confirmation, setConfirmation] =
    useState<PatientReminderPreferenceChange | null>(null)
  const queryKey = [QUERY_ROOT, tenantId, patientAuthorityId]
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const preference = await repository.get(
        tenantId,
        patientAuthorityId,
        signal
      )
      if (!samePatient(preference, tenantId, patientAuthorityId))
        throw new Error('Reminder preference patient mismatch')
      return preference
    },
  })
  const mutation = useMutation({
    mutationFn: async (change: PatientReminderPreferenceChange) => {
      const preference = await repository.update(
        tenantId,
        patientAuthorityId,
        change
      )
      if (!samePatient(preference, tenantId, patientAuthorityId))
        throw new Error('Reminder preference patient mismatch')
      return preference
    },
    onSuccess: (preference) => {
      // The response is the stored value; only other ids for this tenant (the
      // same patient under a re-bound clinic) are read again.
      client.setQueryData(queryKey, preference)
      void client.invalidateQueries({
        queryKey: [QUERY_ROOT, tenantId],
        predicate: (cached) => cached.queryKey[2] !== patientAuthorityId,
      })
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: [QUERY_ROOT, tenantId] })
    },
  })

  const preference = query.isError ? undefined : query.data
  const saving = mutation.isPending
  // Never disabled because the clinic's reminders are off or the number
  // opted out: staff may record a patient's preference before or regardless.
  const switchDisabled =
    !preference ||
    query.isFetching ||
    saving ||
    preference.preferenceState === 'UNREADABLE'

  // A disabled switch cannot hold focus, so after a confirm (or while a refresh
  // runs) focus waits on the card and returns once the switch is usable again,
  // unless staff have moved it elsewhere.
  function returnFocus(now: boolean) {
    if (now) {
      focusSwitch(switchTargetRef.current)
      return
    }
    restoreFocus.current = true
    rootRef.current?.focus()
  }
  useEffect(() => {
    if (!restoreFocus.current || switchDisabled) return
    restoreFocus.current = false
    const active = document.activeElement
    if (
      active === null ||
      active === document.body ||
      active === rootRef.current
    )
      focusSwitch(switchTargetRef.current)
    // The status too: a save answered at once may never render as pending.
  }, [switchDisabled, mutation.status])

  useEffect(() => {
    if (confirmation) cancelRef.current?.focus()
  }, [confirmation])

  function cancel() {
    setConfirmation(null)
    returnFocus(!switchDisabled)
  }

  function confirm() {
    if (!confirmation || saving) return
    const change = confirmation
    setConfirmation(null)
    returnFocus(false)
    mutation.mutate(change)
  }

  const turningOff = confirmation?.remindersEnabled === false
  const context = preference ? contextLines(preference) : []
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className={`${styles.cardBody} ${styles.reminderPreference}`}
      onKeyDown={(event) => {
        // Focus can rest on the card (a click on the confirmation's text):
        // Escape still cancels it there.
        if (event.key !== 'Escape' || !confirmation) return
        event.preventDefault()
        cancel()
      }}
    >
      <div className={styles.reminderControl}>
        {preference ? (
          <span
            className={styles.reminderState}
            data-state={preference.preferenceState}
          >
            {STATE_LABEL[preference.preferenceState]}
          </span>
        ) : (
          <span />
        )}
        <label ref={switchTargetRef} className={styles.reminderSwitchTarget}>
          <Switch
            aria-label={`Appointment reminders for ${displayName}`}
            aria-describedby={statusId}
            checked={preference?.remindersEnabled ?? false}
            disabled={switchDisabled}
            onChange={() => {
              if (!preference || switchDisabled) return
              mutation.reset()
              setConfirmation({
                remindersEnabled: !preference.remindersEnabled,
                expectedRevision: preference.revision,
              })
            }}
          />
        </label>
      </div>
      <p id={statusId} className={styles.reminderStatus} role="status">
        {query.isError
          ? ''
          : !preference
            ? 'Loading reminder setting…'
            : saving
              ? 'Saving…'
              : statusLine(preference)}
      </p>
      {query.isError ? (
        <div className={styles.reminderLoadError}>
          <p className={styles.reminderAlert} role="alert">
            Reminder setting unavailable.
          </p>
          <Button
            type="button"
            variant="secondary"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : null}

      {confirmation ? (
        <div
          className={styles.reminderConfirmation}
          data-direction={turningOff ? 'off' : 'on'}
          role="alertdialog"
          aria-labelledby={confirmationTitleId}
          aria-describedby={confirmationBodyId}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return
            event.preventDefault()
            event.stopPropagation()
            cancel()
          }}
        >
          <h3 id={confirmationTitleId}>
            {turningOff
              ? `Turn off appointment reminders for ${displayName}?`
              : `Turn appointment reminders back on for ${displayName}?`}
          </h3>
          <p id={confirmationBodyId}>
            {turningOff
              ? "Flio won't send this patient appointment reminders at this clinic. A reminder already being sent may still arrive. This isn't an opt-out: other texts still go."
              : "Only do this if the patient wants reminders. Reminders still depend on the clinic's setting and on their number not having opted out."}
          </p>
          <div>
            <Button
              ref={cancelRef}
              type="button"
              variant="secondary"
              onClick={cancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className={turningOff ? styles.reminderDangerButton : undefined}
              disabled={saving}
              onClick={confirm}
            >
              {turningOff ? 'Turn off reminders' : 'Turn on reminders'}
            </Button>
          </div>
        </div>
      ) : null}

      {mutation.isError ? (
        <p className={styles.reminderAlert} role="alert">
          {saveErrorMessage(mutation.error)}
        </p>
      ) : null}

      {context.length ? (
        <ul className={styles.reminderContext}>
          {context.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}
      <p className={styles.reminderFootnote}>{FOOTNOTE}</p>
    </div>
  )
}
